import {
  createRootRoute, createRoute, createRouter,
  RouterProvider, Outlet, redirect,
} from '@tanstack/react-router';
import { Header } from './components/Header.tsx';
import { TopBar } from './components/TopBar.tsx';
import { BottomNav } from './components/BottomNav.tsx';
import { Sidebar } from './components/Sidebar.tsx';
import { Login } from './pages/Login.tsx';
import { AcceptInvite } from './pages/AcceptInvite.tsx';
import { ForgotPassword } from './pages/ForgotPassword.tsx';
import { SsoHandoff } from './pages/SsoHandoff.tsx';
import { Demo } from './pages/Demo.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { Profile } from './pages/Profile.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Inbox } from './pages/Inbox.tsx';
import { AdminOrgs } from './pages/AdminOrgs.tsx';
import { AdminOrgDetail } from './pages/AdminOrgDetail.tsx';
import { OrgUsers } from './pages/OrgUsers.tsx';
import { Settings } from './pages/Settings.tsx';
import { OrgSettings } from './pages/OrgSettings.tsx';
import { NumberSettings } from './pages/NumberSettings.tsx';
import { TermSettings } from './pages/TermSettings.tsx';
import { DataExport } from './pages/DataExport.tsx';
import { Meetings } from './pages/Meetings.tsx';
import { Statistics } from './pages/Statistics.tsx';
import { StatsSubmissions } from './pages/StatsSubmissions.tsx';
import { StatsAttendance } from './pages/StatsAttendance.tsx';
import { StatsSummary } from './pages/StatsSummary.tsx';
import { StatsCompliance } from './pages/StatsCompliance.tsx';
import { MeetingDetail } from './pages/MeetingDetail.tsx';
import { YaumiyyaEditor } from './pages/YaumiyyaEditor.tsx';
import { MeetingNotesLayout } from './pages/MeetingNotesLayout.tsx';
import { MeetingNotePage } from './pages/MeetingNotePage.tsx';
import { Thanfeezu } from './pages/Thanfeezu.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { useAuthStore } from './store/auth.ts';
import { dv } from './i18n/dv.ts';

function RootLayout(): JSX.Element {
  // Sidebar / BottomNav / Header internals already bail when !user, but the
  // <main> element reserves space for the sidebar with md:ms-60. When the user
  // is signed out (Login, AcceptInvite) that offset pushes content right with
  // nothing on the left — so condition the offset on auth state.
  const user = useAuthStore((s) => s.user);
  return (
    <div className={`min-h-screen flex flex-col ${user ? 'bg-[hsl(var(--chrome))]' : ''}`}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:text-sm focus:font-medium"
      >
        މައި ކޮންޓެންޓަށް ދޭ
      </a>
      <TopBar />
      <Sidebar />
      {/* Fixed inverted-corner masks so the white content panel reads as rounded
          against the chrome where the top app bar meets the nav drawer. Painted
          (not a panel border-radius) so the corner stays put as content scrolls.
          RTL: the drawer is on the inline-start (right), so the inner notch sits
          at right-64 and the outer notch at the left edge. */}
      {user && (
        <div
          aria-hidden
          className="hidden md:block fixed top-16 right-64 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom left, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}
      {user && (
        <div
          aria-hidden
          className="hidden md:block fixed top-16 left-0 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom right, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}
      <Header />
      <main
        id="main-content"
        tabIndex={-1}
        className={`flex-1 ${user ? 'has-bottom-nav md:ms-64 md:pt-16' : ''}`}
      >
        {user?.isDemo && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-sm text-amber-900">
            {dv.demo.banner}
            <button
              type="button"
              onClick={() => useAuthStore.getState().logout()}
              className="ms-3 underline font-medium"
            >
              {dv.demo.exit}
            </button>
          </div>
        )}
        {user ? (
          <div className="min-h-dvh bg-[hsl(var(--background))] md:min-h-[calc(100dvh-4rem)]">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </main>
      <BottomNav />
    </div>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const requireAuth = (): void => {
  if (!useAuthStore.getState().accessToken) throw redirect({ to: '/login' });
};

const requireOrgMember = (): void => {
  const state = useAuthStore.getState();
  if (!state.accessToken) throw redirect({ to: '/login' });
  if (!state.user?.orgId) throw redirect({ to: '/' });
};

const requireOrgAdmin = (): void => {
  const state = useAuthStore.getState();
  if (!state.accessToken) throw redirect({ to: '/login' });
  if (!state.user?.isOrgAdmin) throw redirect({ to: '/' });
};

const requireSysAdmin = (): void => {
  const state = useAuthStore.getState();
  if (!state.accessToken) throw redirect({ to: '/login' });
  if (state.user?.role !== 'SYS_ADMIN') throw redirect({ to: '/' });
};

// Public routes
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: Login });
const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/invite/$token', component: AcceptInvite });
const ssoHandoffRoute = createRoute({ getParentRoute: () => rootRoute, path: '/sso/handoff', component: SsoHandoff });
const forgotPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/forgot-password', component: ForgotPassword });
const resetPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/reset-password/$token', component: ResetPassword });
// Public sales/demo entry — no requireAuth: it mints its own throwaway session.
const demoRoute = createRoute({ getParentRoute: () => rootRoute, path: '/demo', component: Demo });

// Authed routes
const indexRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/',
  beforeLoad: requireAuth,
  component: Dashboard,
});
const profileRoute = createRoute({ getParentRoute: () => rootRoute, path: '/profile', beforeLoad: requireAuth, component: Profile });
const notificationsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/notifications', beforeLoad: requireAuth, component: Inbox });

// Authed + orgId routes
const meetingsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/meetings', beforeLoad: requireOrgMember, component: Meetings });
const statisticsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/statistics', beforeLoad: requireOrgMember, component: Statistics });
const statisticsIndexRoute = createRoute({ getParentRoute: () => statisticsRoute, path: '/', beforeLoad: () => { throw redirect({ to: '/statistics/submissions' }); } });
const statisticsSubmissionsRoute = createRoute({ getParentRoute: () => statisticsRoute, path: 'submissions', component: StatsSubmissions });
const statisticsAttendanceRoute = createRoute({ getParentRoute: () => statisticsRoute, path: 'attendance', component: StatsAttendance });
const statisticsSummaryRoute = createRoute({ getParentRoute: () => statisticsRoute, path: 'summary', component: StatsSummary });
const statisticsComplianceRoute = createRoute({ getParentRoute: () => statisticsRoute, path: 'compliance', component: StatsCompliance });
const thanfeezuRoute = createRoute({ getParentRoute: () => rootRoute, path: '/thanfeezu', beforeLoad: requireOrgMember, component: Thanfeezu });
const meetingDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: '/meetings/$meetingId', beforeLoad: requireOrgMember, component: MeetingDetail });
const yaumiyyaRoute = createRoute({ getParentRoute: () => rootRoute, path: '/meetings/$meetingId/yaumiyya', beforeLoad: requireOrgMember, component: YaumiyyaEditor });
// A person's private notebook for a meeting: the shell holds the page list,
// each page is a child route so the editor only ever has ONE live document.
const meetingNotesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/meetings/$meetingId/notes', beforeLoad: requireOrgMember, component: MeetingNotesLayout });
// /notes keeps working — existing links and bookmarks land on the general pad,
// which is where the chair's script lives and what the single notepad used to be.
const meetingNotesIndexRoute = createRoute({
  getParentRoute: () => meetingNotesRoute, path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/meetings/$meetingId/notes/$pageId',
      params: { meetingId: params.meetingId, pageId: 'general' },
    });
  },
});
const meetingNotePageRoute = createRoute({ getParentRoute: () => meetingNotesRoute, path: '$pageId', component: MeetingNotePage });

// Authed + isOrgAdmin routes — Settings shell with nested tabs.
// Old /users bookmarks redirect into the new settings area.
const usersRedirectRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/users',
  beforeLoad: () => { requireOrgAdmin(); throw redirect({ to: '/settings/users' }); },
});
// /dashboard is a common bookmark/typo — the home page lives at '/'. Redirect.
const dashboardRedirectRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/dashboard',
  beforeLoad: () => { throw redirect({ to: '/' }); },
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/settings',
  beforeLoad: requireOrgAdmin, component: Settings,
});
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute, path: '/',
  beforeLoad: () => { throw redirect({ to: '/settings/users' }); },
});
const settingsUsersRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'users', component: OrgUsers });
const settingsOrgRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'org', component: OrgSettings });
const settingsNumbersRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'numbers', component: NumberSettings });
const settingsTermsRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'terms', component: TermSettings });
const settingsExportRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'export', component: DataExport });

// SYS_ADMIN routes
const adminOrgsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/admin/orgs', beforeLoad: requireSysAdmin, component: AdminOrgs });
const adminOrgDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: '/admin/orgs/$orgId', beforeLoad: requireSysAdmin, component: AdminOrgDetail });

const routeTree = rootRoute.addChildren([
  loginRoute,
  ssoHandoffRoute, inviteRoute, forgotPasswordRoute, resetPasswordRoute, demoRoute,
  indexRoute, profileRoute, notificationsRoute,
  meetingsRoute, meetingDetailRoute, yaumiyyaRoute,
  meetingNotesRoute.addChildren([meetingNotesIndexRoute, meetingNotePageRoute]),
  thanfeezuRoute,
  statisticsRoute.addChildren([statisticsIndexRoute, statisticsSubmissionsRoute, statisticsAttendanceRoute, statisticsSummaryRoute, statisticsComplianceRoute]),
  usersRedirectRoute, dashboardRedirectRoute,
  settingsRoute.addChildren([settingsIndexRoute, settingsUsersRoute, settingsOrgRoute, settingsTermsRoute, settingsNumbersRoute, settingsExportRoute]),
  adminOrgsRoute, adminOrgDetailRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}

export function AppRouter(): JSX.Element { return <RouterProvider router={router} />; }
